import React, { useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { AppContext } from '../../../../../RoutesWithAuth';
import { UserPreferencesContext } from '../../../../common/UserPreferences';
import axios from '../../../../common/AxiosConfig';
import CustomGrid, { applyFilter } from '../../customgrid';
import EditDialog from '../../customgrid/EditDialog';
import { makeStyles } from "@mui/styles";
import { IconButton, Tooltip } from '@mui/material';
import { Edit } from '@mui/icons-material';
import { LoadingIndicator } from '../../reports/reconcilationreport/MuiStyled';
import { ADMIN_PERMISSIONS } from '../../../../common/RolePermissions';
import PermissionButton from '../../PermissionButton';

const emailValidation = (email) => {
    if (!email) return true;
    const singleEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    return email.split(',').every(e => singleEmailRegex.test(e.trim()));
};

const emailValidator = (value) => {
    if (value && !emailValidation(value)) {
        return 'Invalid email format';
    }
    return null;
};

const EDIT_FIELDS = [
    { name: 'app_name', label: 'Application Name', type: 'text', disabled: true },
    { name: 'primary_contact_name', label: 'Primary Contact Name', type: 'text', required: true },
    { name: 'primary_contact_email', label: 'Primary Contact Email', type: 'text', required: true, validator: emailValidator },
    { name: 'escalation_contact_name', label: 'Escalation Contact Name', type: 'text', required: true },
    { name: 'escalation_contact_email', label: 'Escalation Contact Email', type: 'text', required: true, validator: emailValidator },
    { name: 'team_email', label: 'Team Email', type: 'text', validator: emailValidator }
];

const useStyles = makeStyles(() => ({
    contactsWrapper: {
        height: 'calc( 100% - 100px )'
    }
}));

function Contacts(props) {
    const [appsData, setAppsData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSavingPreferences, setIsSavingPreferences] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingRow, setEditingRow] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageCount, setPageCount] = useState(20);
    const [activeFilters, setActiveFilters] = useState({});
    const [activeSortModel, setActiveSortModel] = useState([]);
    const [defaultHiddenColumns, setDefaultHiddenColumns] = useState({});

    const context = useContext(AppContext);
    const authToken = context.authToken.get;
    const isAdmin = context.isAdmin.get;
    const { getPreferences, savePreferences, loaded: prefsLoaded } = useContext(UserPreferencesContext);
    const classes = useStyles();

    // Load saved preferences on mount
    useEffect(() => {
        if (!prefsLoaded) return;
        const prefs = getPreferences('CONTACTS');
        if (prefs.filters && Object.keys(prefs.filters).length > 0) setActiveFilters(prefs.filters);
        if (prefs.sort && prefs.sort.field) setActiveSortModel([prefs.sort]);
        if (prefs.hiddenColumns && Object.keys(prefs.hiddenColumns).length > 0) setDefaultHiddenColumns(prefs.hiddenColumns);
        if (prefs.pageCount) setPageCount(prefs.pageCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prefsLoaded]);

    useEffect(() => {
        fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authToken]);

    const fetchData = () => {
        setIsLoading(true);
        axios.get(`contacts`, {
            headers: { Authorization: authToken }
        }).then(res => {
            setIsLoading(false);
            setAppsData(res.data);
        }).catch(err => {
            setIsLoading(false);
            console.log(err);
        });
    };

    const updateApp = (rowInfo) => {
        axios.put(`/contacts/${rowInfo.app_name}`, {
            primary_name: rowInfo.primary_contact_name.trim(),
            primary_email: rowInfo.primary_contact_email.trim(),
            escalation_name: rowInfo.escalation_contact_name.trim(),
            escalation_email: rowInfo.escalation_contact_email.trim(),
            escalation_team_email: rowInfo.team_email?.trim()
        }, {
            headers: { Authorization: authToken }
        }).then(() => {
            fetchData();
        }).catch(err => {
            props.toggleError(err.response.data.message);
        });
    };

    const handleEditClick = useCallback((row) => {
        setEditingRow(row);
        setEditDialogOpen(true);
    }, []);

    const handleEditSave = useCallback((formValues, originalRow) => {
        updateApp({ ...originalRow, ...formValues });
        setEditDialogOpen(false);
        setEditingRow(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authToken]);

    const handleEditClose = useCallback(() => {
        setEditDialogOpen(false);
        setEditingRow(null);
    }, []);

    const appsGridData = useMemo(() => {
        return appsData?.map((row) => ({
            id: row.app_name,
            app_name: row.app_name,
            app_status: row.app_status === 'Yes' ? 'Yes' : row.app_status === 'No' ? 'No' : '',
            primary_contact_name: row.data.primary.name,
            primary_contact_email: row.data.primary.email,
            escalation_contact_name: row.data.escalation.name,
            escalation_contact_email: row.data.escalation.email,
            team_email: row.data.escalation.team_email
        })) || [];
    }, [appsData]);

    const columns = useMemo(() => {
        const cols = [
            { name: "#", mapping: "rowId", width: 50, frozen: true, sortable: false, hideable: false, disableColumnMenu: true, renderCell: ({ rowIndex }) => rowIndex + 1 },
            {
                name: "Application Name", mapping: "app_name", width: 190, frozen: true,
                sortable: true, type: 'singleSelect', hideable: false,
                valueOptions: appsData?.map(({ app_name }) => app_name),
                displayTootip: ["app_name"]
            },
            {
                name: "App Status", mapping: "app_status", width: 120,
                sortable: true, type: 'singleSelect',
                valueOptions: ['Yes', 'No', ''],
                displayTootip: ["app_status"]
            },
            {
                name: "Primary Contact Name", mapping: "primary_contact_name", width: 180,
                sortable: true, displayTootip: ["primary_contact_name"]
            },
            {
                name: "Primary Contact Email", mapping: "primary_contact_email", width: 190,
                sortable: true, displayTootip: ["primary_contact_email"]
            },
            {
                name: "Escalation Contact Name", mapping: "escalation_contact_name", width: 180,
                sortable: true, displayTootip: ["escalation_contact_name"]
            },
            {
                name: "Escalation Contact Email", mapping: "escalation_contact_email", width: 190,
                sortable: true, displayTootip: ["escalation_contact_email"]
            },
            {
                name: "Team Email", mapping: "team_email", width: 200,
                sortable: true, displayTootip: ["team_email"]
            }
        ];


            cols.push({
                name: "Edit", mapping: "actions", width: 80, sortable: false,
                disableColumnMenu: true, hideable: false,
                renderCell: ({ row }) => (
                    <Tooltip title="Edit">
                        <PermissionButton iconOnly permission={ADMIN_PERMISSIONS.CONTACTS_EDIT} size="small" onClick={(e) => { e.stopPropagation(); handleEditClick(row); }}>
                            <Edit fontSize="small" />
                        </PermissionButton>
                    </Tooltip>
                )
            });


        return cols;
    }, [appsData, isAdmin, handleEditClick]);

    const filteredAndSortedData = useMemo(() => {
        let result = [...appsGridData];

        // Apply filters
        Object.keys(activeFilters).forEach(field => {
            const filter = activeFilters[field];
            if (filter && filter.value) {
                const column = columns.find(c => c.mapping === field);
                result = result.filter(row => {
                    return applyFilter(row[field], filter.value, filter.operatorValue, column?.type);
                });
            }
        });

        // Apply sorting
        if (activeSortModel.length > 0) {
            const sort = activeSortModel[0];
            result.sort((a, b) => {
                const aVal = a[sort.field];
                const bVal = b[sort.field];
                if (aVal === bVal) return 0;
                if (aVal === null || aVal === undefined) return 1;
                if (bVal === null || bVal === undefined) return -1;
                const comparison = String(aVal).toLowerCase() < String(bVal).toLowerCase() ? -1 : 1;
                return sort.sort === 'asc' ? comparison : -comparison;
            });
        }

        return result;
    }, [appsGridData, activeFilters, activeSortModel, columns]);

    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * pageCount;
        return filteredAndSortedData.slice(start, start + pageCount).map((row, i) => ({
            ...row,
            rowId: start + i + 1
        }));
    }, [filteredAndSortedData, currentPage, pageCount]);

    const handleRemoteFiltering = useCallback((filters) => {
        setActiveFilters(filters);
        setCurrentPage(1);
    }, []);

    const handleRemoteSorting = useCallback((sortModel) => {
        setActiveSortModel(sortModel);
        setCurrentPage(1);
    }, []);

    const handleResetAllFilters = useCallback(() => {
        setActiveFilters({});
        setActiveSortModel([]);
        setCurrentPage(1);
    }, []);

    const handleSaveDefaults = useCallback((gridState) => {
        setIsSavingPreferences(true);
        const startTime = Date.now();
        
        return savePreferences('CONTACTS', {
            filters: gridState.filters,
            sort: gridState.sortModel?.[0] || {},
            hiddenColumns: gridState.hiddenColumns,
            pageCount: gridState.pageCount
        }).then(() => {
            // Ensure loader shows for at least 2 seconds
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(0, 2000 - elapsedTime);
            
            setTimeout(() => {
                setIsSavingPreferences(false);
                props.toggleError('Filter preferences saved successfully', 'success');
            }, remainingTime);
        }).catch(() => {
            // Ensure loader shows for at least 2 seconds even on error
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(0, 2000 - elapsedTime);
            
            setTimeout(() => {
                setIsSavingPreferences(false);
                props.toggleError('Failed to save filter preferences', 'error');
            }, remainingTime);
        });
    }, [savePreferences, props]);

    return (
        <div className={classes.contactsWrapper}>
            {(isLoading || isSavingPreferences) && (<LoadingIndicator />)}
            <CustomGrid
                columns={columns}
                rows={paginatedData}
                customHeight={"100%"}
                enableCustomFiltering={true}
                showFilterButton={true}
                showColumnSelector={true}
                remoteFiltering={true}
                applyRemoteFiltering={handleRemoteFiltering}
                applyRemoteSorting={handleRemoteSorting}
                defaultFilters={activeFilters}
                defaultSortModel={activeSortModel}
                onResetAllFilters={handleResetAllFilters}
                showPagination={true}
                currentPage={currentPage}
                pageCount={pageCount}
                totalResults={filteredAndSortedData.length}
                onPageChange={(page) => setCurrentPage(page)}
                onPageCountChange={(count) => {
                    setPageCount(count);
                    setCurrentPage(1);
                }}
                defaultHiddenColumns={defaultHiddenColumns}
                onSaveDefaults={handleSaveDefaults}
            />
            <EditDialog
                open={editDialogOpen}
                onClose={handleEditClose}
                onSave={handleEditSave}
                row={editingRow}
                fields={EDIT_FIELDS}
                title="Edit Contact"
            />
        </div>
    );
}
export default Contacts;